import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiService } from '../services/api';
import { 
  UserStoryPreferences, 
  UserStoryPreferencesUpdate, 
  SavedSetting, 
  SavedSettingCreate, 
  SavedSettingUpdate,
  SavedCharacter,
  SavedCharacterCreate,
  SavedCharacterUpdate
} from '../types/workshop';
import { logger } from '../utils/logger';
import '../styles/pages/StoryWorkshop.css';

// Form modes
type FormMode = 'create' | 'edit' | 'view';

const StoryWorkshop: React.FC = () => {
  // Settings state
  const [savedSettings, setSavedSettings] = useState<SavedSetting[]>([]);
  const [loadingSettings, setLoadingSettings] = useState<boolean>(true);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settingFormMode, setSettingFormMode] = useState<FormMode>('view');
  const [currentSetting, setCurrentSetting] = useState<SavedSettingCreate>({
    name: '',
    setting: '',
    description: '',
  });
  const [editSettingId, setEditSettingId] = useState<string | null>(null);
  
  // Characters state
  const [savedCharacters, setSavedCharacters] = useState<SavedCharacter[]>([]);
  const [loadingCharacters, setLoadingCharacters] = useState<boolean>(true);
  const [charactersError, setCharactersError] = useState<string | null>(null);
  const [characterFormMode, setCharacterFormMode] = useState<FormMode>('view');
  const [currentCharacter, setCurrentCharacter] = useState<SavedCharacterCreate>({
    name: '',
    character: '',
    description: '',
  });
  const [editCharacterId, setEditCharacterId] = useState<string | null>(null);
  
  // Preferences state
  const [preferences, setPreferences] = useState<UserStoryPreferencesUpdate>({
    default_mood: '',
    default_length: '',
    default_voice: ''
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingPreferences, setLoadingPreferences] = useState<boolean>(true);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Load data on component mount
  useEffect(() => {
    loadSavedSettings();
    loadSavedCharacters();
    loadPreferences();
  }, []);

  // Load saved settings
  const loadSavedSettings = async () => {
    try {
      setLoadingSettings(true);
      setSettingsError(null);
      const settings = await ApiService.getSavedSettings();
      setSavedSettings(settings);
    } catch (err) {
      logger.error('Error loading saved settings:', err);
      setSettingsError('Failed to load your saved settings. Please try again.');
    } finally {
      setLoadingSettings(false);
    }
  };
  
  // Load saved characters
  const loadSavedCharacters = async () => {
    try {
      setLoadingCharacters(true);
      setCharactersError(null);
      const characters = await ApiService.getSavedCharacters();
      setSavedCharacters(characters);
    } catch (err) {
      logger.error('Error loading saved characters:', err);
      setCharactersError('Failed to load your saved characters. Please try again.');
    } finally {
      setLoadingCharacters(false);
    }
  };

  // Load user preferences
  const loadPreferences = async () => {
    try {
      setLoadingPreferences(true);
      const userPrefs = await ApiService.getUserStoryPreferences();
      
      if (userPrefs) {
        setPreferences({
          default_mood: userPrefs.default_mood || '',
          default_length: userPrefs.default_length || '',
          default_voice: userPrefs.default_voice || ''
        });
      }
    } catch (err) {
      logger.error('Error loading user preferences:', err);
      setError('Failed to load your preferences. Please try again.');
    } finally {
      setLoadingPreferences(false);
    }
  };

  // Handle input changes for preferences
  const handlePreferenceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setPreferences(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Save user preferences
  const handleSavePreferences = async () => {
    try {
      setLoading(true);
      setError(null);
      
      await ApiService.saveUserStoryPreferences(preferences);
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      logger.error('Error saving preferences:', err);
      setError('Failed to save your preferences. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes for settings form
  const handleSettingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    
    setCurrentSetting(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Handle input changes for character form
  const handleCharacterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    
    setCurrentCharacter(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Show the create setting form
  const showCreateSettingForm = () => {
    setSettingFormMode('create');
    setCurrentSetting({
      name: '',
      setting: '',
      description: '',
    });
    setEditSettingId(null);
  };
  
  // Show the create character form
  const showCreateCharacterForm = () => {
    setCharacterFormMode('create');
    setCurrentCharacter({
      name: '',
      character: '',
      description: '',
    });
    setEditCharacterId(null);
  };

  // Show the edit setting form
  const showEditSettingForm = (setting: SavedSetting) => {
    setSettingFormMode('edit');
    setCurrentSetting({
      name: setting.name,
      setting: setting.setting,
      description: setting.description || '',
    });
    setEditSettingId(setting.id);
  };
  
  // Show the edit character form
  const showEditCharacterForm = (character: SavedCharacter) => {
    setCharacterFormMode('edit');
    setCurrentCharacter({
      name: character.name,
      character: character.character,
      description: character.description || '',
    });
    setEditCharacterId(character.id);
  };

  // Cancel setting form
  const cancelSettingForm = () => {
    setSettingFormMode('view');
    setCurrentSetting({
      name: '',
      setting: '',
      description: '',
    });
    setEditSettingId(null);
  };
  
  // Cancel character form
  const cancelCharacterForm = () => {
    setCharacterFormMode('view');
    setCurrentCharacter({
      name: '',
      character: '',
      description: '',
    });
    setEditCharacterId(null);
  };

  // Save setting
  const saveSetting = async () => {
    try {
      setLoading(true);
      setSettingsError(null);
      
      if (settingFormMode === 'create') {
        await ApiService.createSavedSetting(currentSetting);
      } else if (settingFormMode === 'edit' && editSettingId) {
        await ApiService.updateSavedSetting(editSettingId, currentSetting);
      }
      
      // Reload settings
      await loadSavedSettings();
      
      // Reset form
      setSettingFormMode('view');
      setCurrentSetting({
        name: '',
        setting: '',
        description: '',
      });
      setEditSettingId(null);
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      logger.error('Error saving setting:', err);
      setSettingsError('Failed to save your setting. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Save character
  const saveCharacter = async () => {
    try {
      setLoading(true);
      setCharactersError(null);
      
      if (characterFormMode === 'create') {
        await ApiService.createSavedCharacter(
          currentCharacter.name,
          currentCharacter.character,
          currentCharacter.description
        );
      } else if (characterFormMode === 'edit' && editCharacterId) {
        await ApiService.updateSavedCharacter(editCharacterId, {
          name: currentCharacter.name,
          character: currentCharacter.character,
          description: currentCharacter.description
        });
      }
      
      // Reload characters
      await loadSavedCharacters();
      
      // Reset form
      setCharacterFormMode('view');
      setCurrentCharacter({
        name: '',
        character: '',
        description: '',
      });
      setEditCharacterId(null);
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      logger.error('Error saving character:', err);
      setCharactersError('Failed to save your character. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Delete setting
  const deleteSetting = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this setting?')) {
      try {
        setLoading(true);
        setSettingsError(null);
        
        await ApiService.deleteSavedSetting(id);
        
        // Reload settings
        await loadSavedSettings();
        
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } catch (err) {
        logger.error('Error deleting setting:', err);
        setSettingsError('Failed to delete your setting. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };
  
  // Delete character
  const deleteCharacter = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this character?')) {
      try {
        setLoading(true);
        setCharactersError(null);
        
        await ApiService.deleteSavedCharacter(id);
        
        // Reload characters
        await loadSavedCharacters();
        
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } catch (err) {
        logger.error('Error deleting character:', err);
        setCharactersError('Failed to delete your character. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  // Render the user preferences section
  const renderPreferencesSection = () => {
    return (
      <div className="workshop-card preferences-card">
        <div className="section-header-row">
          <h2 className="section-title">
            Story Preferences
          </h2>
          <button 
            className="action-button primary save-preferences-button"
            onClick={handleSavePreferences}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
        
        {loadingPreferences ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading your preferences...</p>
          </div>
        ) : (
          <div className="preferences-form">
            {error && <div className="error-message">{error}</div>}
            {saveSuccess && <div className="success-message">Preferences saved successfully!</div>}
            
            <div className="form-group">
              <label htmlFor="default_mood">Default Mood</label>
              <select 
                id="default_mood" 
                name="default_mood"
                className="form-select"
                value={preferences.default_mood}
                onChange={handlePreferenceChange}
              >
                <option value="">Select a default mood</option>
                <option value="happy">Happy</option>
                <option value="calm">Calm</option>
                <option value="adventurous">Adventurous</option>
                <option value="mysterious">Mysterious</option>
                <option value="educational">Educational</option>
              </select>
              <p className="help-text">This mood will be pre-selected when creating new stories.</p>
            </div>
            
            <div className="form-group">
              <label htmlFor="default_length">Default Story Length</label>
              <select 
                id="default_length" 
                name="default_length"
                className="form-select"
                value={preferences.default_length}
                onChange={handlePreferenceChange}
              >
                <option value="">Select a default length</option>
                <option value="short">Short (1-2 minutes)</option>
                <option value="medium">Medium (3-5 minutes)</option>
                <option value="long">Long (6-8 minutes)</option>
              </select>
              <p className="help-text">This length will be pre-selected when creating new stories.</p>
            </div>
            
            <div className="form-group">
              <label htmlFor="default_voice">Default Narrator Voice</label>
              <select 
                id="default_voice" 
                name="default_voice"
                className="form-select"
                value={preferences.default_voice}
                onChange={handlePreferenceChange}
              >
                <option value="">Select a default voice</option>
                <option value="friendly">Friendly</option>
                <option value="gentle">Gentle</option>
                <option value="playful">Playful</option>
                <option value="wise">Wise</option>
              </select>
              <p className="help-text">This voice will be pre-selected when creating new stories.</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render the settings section
  const renderSettingsSection = () => {
    return (
      <div className="workshop-card settings-card">
        <div className="section-header-row">
          <h2 className="section-title">
            Story Settings
          </h2>
          {settingFormMode === 'view' && (
            <button 
              className="action-button primary"
              onClick={showCreateSettingForm}
            >
              <span className="button-icon">+</span> Add New Setting
            </button>
          )}
        </div>
        
        {settingsError && <div className="error-message">{settingsError}</div>}
        {saveSuccess && <div className="success-message">Setting saved successfully!</div>}
        
        {settingFormMode !== 'view' && (
          <div className="setting-form">
            <h3>{settingFormMode === 'create' ? 'Create New Setting' : 'Edit Setting'}</h3>
            
            <div className="form-group">
              <label htmlFor="name">Setting Name</label>
              <input 
                type="text" 
                id="name" 
                name="name"
                className="form-input"
                value={currentSetting.name}
                onChange={handleSettingChange}
                placeholder="e.g., Enchanted Forest"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Description (Optional)</label>
              <input 
                type="text" 
                id="description" 
                name="description"
                className="form-input"
                value={currentSetting.description || ''}
                onChange={handleSettingChange}
                placeholder="Brief description of this setting"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="setting-details">Setting Details</label>
              <textarea 
                id="setting-details" 
                name="setting"
                className="form-textarea"
                value={currentSetting.setting}
                onChange={handleSettingChange}
                placeholder="Describe the setting in detail"
                rows={4}
                required
              />
            </div>
            
            <div className="form-actions">
              <button 
                className="action-button secondary"
                onClick={cancelSettingForm}
              >
                Cancel
              </button>
              <button 
                className="action-button primary"
                onClick={saveSetting}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Setting'}
              </button>
            </div>
          </div>
        )}
        
        {settingFormMode === 'view' && (
          <>
            {loadingSettings ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading your settings...</p>
              </div>
            ) : savedSettings.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">No Settings</div>
                <h3>No Saved Settings Yet</h3>
                <p>Save story settings as you create stories, or add them manually.</p>
              </div>
            ) : (
              <div className="settings-grid">
                {savedSettings.map(setting => (
                  <div key={setting.id} className="setting-card">
                    <div className="setting-card-header">
                      <h3 className="setting-name">{setting.name}</h3>
                    </div>
                    {setting.description && <p className="setting-description">{setting.description}</p>}
                    <p className="setting-details">{setting.setting}</p>
                    <div className="setting-card-actions">
                      <button 
                        className="action-button secondary small"
                        onClick={() => showEditSettingForm(setting)}
                      >
                        Edit
                      </button>
                      <button 
                        className="action-button danger small"
                        onClick={() => deleteSetting(setting.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Render the characters section
  const renderCharactersSection = () => {
    return (
      <div className="workshop-card characters-card">
        <div className="section-header-row">
          <h2 className="section-title">
            Story Characters
          </h2>
          {characterFormMode === 'view' && (
            <button 
              className="action-button primary"
              onClick={showCreateCharacterForm}
            >
              <span className="button-icon">+</span> Add New Character
            </button>
          )}
        </div>
        
        {charactersError && <div className="error-message">{charactersError}</div>}
        {saveSuccess && <div className="success-message">Character saved successfully!</div>}
        
        {characterFormMode !== 'view' && (
          <div className="setting-form character-form">
            <h3>{characterFormMode === 'create' ? 'Create New Character' : 'Edit Character'}</h3>
            
            <div className="form-group">
              <label htmlFor="character-name">Character Name</label>
              <input 
                type="text" 
                id="character-name" 
                name="name"
                className="form-input"
                value={currentCharacter.name}
                onChange={handleCharacterChange}
                placeholder="e.g., Luna the Unicorn"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="character-description">Description (Optional)</label>
              <input 
                type="text" 
                id="character-description" 
                name="description"
                className="form-input"
                value={currentCharacter.description || ''}
                onChange={handleCharacterChange}
                placeholder="Brief description of this character"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="character-details">Character Details</label>
              <textarea 
                id="character-details" 
                name="character"
                className="form-textarea"
                value={currentCharacter.character}
                onChange={handleCharacterChange}
                placeholder="Describe the character in detail (personality, appearance, etc.)"
                rows={4}
                required
              />
            </div>
            
            <div className="form-actions">
              <button 
                className="action-button secondary"
                onClick={cancelCharacterForm}
              >
                Cancel
              </button>
              <button 
                className="action-button primary"
                onClick={saveCharacter}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Character'}
              </button>
            </div>
          </div>
        )}
        
        {characterFormMode === 'view' && (
          <>
            {loadingCharacters ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading your characters...</p>
              </div>
            ) : savedCharacters.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">No Characters</div>
                <h3>No Saved Characters Yet</h3>
                <p>Save characters as you create stories, or add them manually.</p>
              </div>
            ) : (
              <div className="settings-grid characters-grid">
                {savedCharacters.map(character => (
                  <div key={character.id} className="setting-card character-card">
                    <div className="setting-card-header">
                      <h3 className="setting-name">{character.name}</h3>
                    </div>
                    {character.description && <p className="setting-description">{character.description}</p>}
                    <p className="setting-details">{character.character}</p>
                    <div className="setting-card-actions">
                      <button 
                        className="action-button secondary small"
                        onClick={() => showEditCharacterForm(character)}
                      >
                        Edit
                      </button>
                      <button 
                        className="action-button danger small"
                        onClick={() => deleteCharacter(character.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="page-container workshop-container">
      <div className="workshop-header">
        <h1 className="page-title">Story Workshop</h1>
        <p className="page-description">
          Manage your story elements and preferences to create personalized bedtime stories quickly.
        </p>
      </div>

      <div className="workshop-content">
        {renderPreferencesSection()}
        {renderSettingsSection()}
        {renderCharactersSection()}
      </div>
    </div>
  );
};

export default StoryWorkshop; 